/**
 * Translation Service
 * Provides multilingual support for NIL player cards and Ask SLAM AI chat interface.
 * Static translations for en/es/ja/ko; 38 languages supported for Gemini response language selection.
 */

// Static translations for UI elements
// In production, could be enhanced with Google Cloud Translation API
const TRANSLATIONS = {
  en: {
    // Player Card
    projectedEarnings: "Projected Annual Earnings",
    nilScore: "NIL Score",
    valuationRange: "Valuation Range",
    baseValue: "Base Value",
    maxProjected: "Max Projected",
    riskAdjusted: "Risk Adjusted",
    socialMetrics: "Social Metrics",
    followers: "Followers",
    engagement: "Engagement",
    cpmRate: "CPM Rate",
    performanceMultipliers: "Performance Multipliers",
    gpa: "GPA",
    athleticAchievements: "Athletic Achievements",
    teamSuccess: "Team Success",
    complianceStatus: "Compliance Status",
    compliant: "Compliant",
    review: "Under Review",
    warning: "Warning",
    houseSettlement: "House Settlement Context",
    capAllocation: "Cap Allocation",
    position: "Position",
    team: "Team",

    // NIL Dashboard
    nilDashboard: "NIL Dashboard",
    totalValue: "Total NIL Value",
    activeDeals: "Active Deals",
    pendingReview: "Pending Review",
    complianceScore: "Compliance Score",
    rosterValuation: "Roster Valuation",
    dealPipeline: "Deal Pipeline",
    budgetAllocation: "Budget Allocation",
    performanceAnalytics: "Performance Analytics",
    complianceCenter: "Compliance Center",

    // Common
    searchAthletes: "Search athletes...",
    viewDetails: "View Details",
    approve: "Approve",
    reject: "Reject",
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    lastUpdated: "Last Updated",

    // Chat UI (Ask SLAM AI)
    askAnything: "Ask anything...",
    whatCanIHelp: "What can I help with?",
    analyzeData: "Ask questions, analyze data, or get strategic insights",
    clearChat: "Clear Chat",
    showCanvas: "Show Canvas",
    hideCanvas: "Hide Canvas",
    agentMode: "Agent",
    sendMessage: "Send",
    uploadFile: "Upload File",
    modelSelector: "Model",
    languageSelector: "Language",
    thinking: "Thinking...",
    toolCall: "Tool Call",
    generating: "Generating response...",
    analyzingQuery: "Analyzing your question...",
    complete: "Complete",
    moreLanguages: "More languages...",
  },
  es: {
    // Player Card
    projectedEarnings: "Ganancias Anuales Proyectadas",
    nilScore: "Puntuacion NIL",
    valuationRange: "Rango de Valoracion",
    baseValue: "Valor Base",
    maxProjected: "Maximo Proyectado",
    riskAdjusted: "Ajustado por Riesgo",
    socialMetrics: "Metricas Sociales",
    followers: "Seguidores",
    engagement: "Interaccion",
    cpmRate: "Tasa CPM",
    performanceMultipliers: "Multiplicadores de Rendimiento",
    gpa: "Promedio Academico",
    athleticAchievements: "Logros Atleticos",
    teamSuccess: "Exito del Equipo",
    complianceStatus: "Estado de Cumplimiento",
    compliant: "Cumple",
    review: "En Revision",
    warning: "Advertencia",
    houseSettlement: "Contexto del Acuerdo House",
    capAllocation: "Asignacion de Tope",
    position: "Posicion",
    team: "Equipo",

    // NIL Dashboard
    nilDashboard: "Panel NIL",
    totalValue: "Valor NIL Total",
    activeDeals: "Acuerdos Activos",
    pendingReview: "Revision Pendiente",
    complianceScore: "Puntuacion de Cumplimiento",
    rosterValuation: "Valoracion del Plantel",
    dealPipeline: "Pipeline de Acuerdos",
    budgetAllocation: "Asignacion de Presupuesto",
    performanceAnalytics: "Analiticas de Rendimiento",
    complianceCenter: "Centro de Cumplimiento",

    // Common
    searchAthletes: "Buscar atletas...",
    viewDetails: "Ver Detalles",
    approve: "Aprobar",
    reject: "Rechazar",
    pending: "Pendiente",
    approved: "Aprobado",
    rejected: "Rechazado",
    lastUpdated: "Ultima Actualizacion",

    // Chat UI (Ask SLAM AI)
    askAnything: "Pregunta lo que quieras...",
    whatCanIHelp: "¿En qué puedo ayudarte?",
    analyzeData: "Haz preguntas, analiza datos u obtén información estratégica",
    clearChat: "Limpiar Chat",
    showCanvas: "Mostrar Canvas",
    hideCanvas: "Ocultar Canvas",
    agentMode: "Agente",
    sendMessage: "Enviar",
    uploadFile: "Subir Archivo",
    modelSelector: "Modelo",
    languageSelector: "Idioma",
    thinking: "Pensando...",
    toolCall: "Llamada de Herramienta",
    generating: "Generando respuesta...",
    analyzingQuery: "Analizando tu pregunta...",
    complete: "Completo",
    moreLanguages: "Más idiomas...",
  },
  ja: {
    // Player Card
    projectedEarnings: "年間予想収益",
    nilScore: "NILスコア",
    valuationRange: "評価範囲",
    baseValue: "基本価値",
    maxProjected: "最大予想",
    riskAdjusted: "リスク調整済み",
    socialMetrics: "ソーシャルメトリクス",
    followers: "フォロワー",
    engagement: "エンゲージメント",
    cpmRate: "CPMレート",
    performanceMultipliers: "パフォーマンス倍率",
    gpa: "成績平均点",
    athleticAchievements: "スポーツ実績",
    teamSuccess: "チーム成績",
    complianceStatus: "コンプライアンス状況",
    compliant: "適合",
    review: "審査中",
    warning: "警告",
    houseSettlement: "ハウス和解の文脈",
    capAllocation: "上限配分",
    position: "ポジション",
    team: "チーム",

    // NIL Dashboard
    nilDashboard: "NILダッシュボード",
    totalValue: "NIL総額",
    activeDeals: "アクティブな契約",
    pendingReview: "審査待ち",
    complianceScore: "コンプライアンススコア",
    rosterValuation: "ロスター評価",
    dealPipeline: "契約パイプライン",
    budgetAllocation: "予算配分",
    performanceAnalytics: "パフォーマンス分析",
    complianceCenter: "コンプライアンスセンター",

    // Common
    searchAthletes: "選手を検索...",
    viewDetails: "詳細を見る",
    approve: "承認",
    reject: "却下",
    pending: "保留中",
    approved: "承認済み",
    rejected: "却下済み",
    lastUpdated: "最終更新",

    // Chat UI (Ask SLAM AI)
    askAnything: "何でも聞いてください...",
    whatCanIHelp: "何かお手伝いできますか？",
    analyzeData: "質問、データ分析、戦略的インサイトの取得",
    clearChat: "チャットをクリア",
    showCanvas: "キャンバスを表示",
    hideCanvas: "キャンバスを非表示",
    agentMode: "エージェント",
    sendMessage: "送信",
    uploadFile: "ファイルアップロード",
    modelSelector: "モデル",
    languageSelector: "言語",
    thinking: "考え中...",
    toolCall: "ツール呼び出し",
    generating: "回答を生成中...",
    analyzingQuery: "質問を分析中...",
    complete: "完了",
    moreLanguages: "その他の言語...",
  },
  ko: {
    // Player Card
    projectedEarnings: "연간 예상 수익",
    nilScore: "NIL 점수",
    valuationRange: "가치 평가 범위",
    baseValue: "기본 가치",
    maxProjected: "최대 예상",
    riskAdjusted: "위험 조정",
    socialMetrics: "소셜 지표",
    followers: "팔로워",
    engagement: "참여도",
    cpmRate: "CPM 비율",
    performanceMultipliers: "성과 배수",
    gpa: "학점 평균",
    athleticAchievements: "운동 성과",
    teamSuccess: "팀 성적",
    complianceStatus: "규정 준수 상태",
    compliant: "준수",
    review: "검토 중",
    warning: "경고",
    houseSettlement: "하우스 합의 맥락",
    capAllocation: "상한 배분",
    position: "포지션",
    team: "팀",

    // NIL Dashboard
    nilDashboard: "NIL 대시보드",
    totalValue: "NIL 총 가치",
    activeDeals: "활성 계약",
    pendingReview: "검토 대기",
    complianceScore: "규정 준수 점수",
    rosterValuation: "로스터 평가",
    dealPipeline: "계약 파이프라인",
    budgetAllocation: "예산 배분",
    performanceAnalytics: "성과 분석",
    complianceCenter: "규정 준수 센터",

    // Common
    searchAthletes: "선수 검색...",
    viewDetails: "상세 보기",
    approve: "승인",
    reject: "거부",
    pending: "대기 중",
    approved: "승인됨",
    rejected: "거부됨",
    lastUpdated: "최근 업데이트",

    // Chat UI (Ask SLAM AI)
    askAnything: "무엇이든 물어보세요...",
    whatCanIHelp: "무엇을 도와드릴까요?",
    analyzeData: "질문하고, 데이터를 분석하고, 전략적 인사이트를 얻으세요",
    clearChat: "채팅 지우기",
    showCanvas: "캔버스 표시",
    hideCanvas: "캔버스 숨기기",
    agentMode: "에이전트",
    sendMessage: "보내기",
    uploadFile: "파일 업로드",
    modelSelector: "모델",
    languageSelector: "언어",
    thinking: "생각 중...",
    toolCall: "도구 호출",
    generating: "응답 생성 중...",
    analyzingQuery: "질문을 분석하는 중...",
    complete: "완료",
    moreLanguages: "더 많은 언어...",
  },
};

// Language metadata - All 38 languages supported by Google Gemini
// Reference: https://docs.cloud.google.com/gemini/docs/codeassist/supported-languages
export const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English", nativeName: "English", flag: "🇺🇸" },
  { code: "es", name: "Spanish", nativeName: "Español", flag: "🇪🇸" },
  { code: "fr", name: "French", nativeName: "Français", flag: "🇫🇷" },
  { code: "de", name: "German", nativeName: "Deutsch", flag: "🇩🇪" },
  { code: "it", name: "Italian", nativeName: "Italiano", flag: "🇮🇹" },
  { code: "pt", name: "Portuguese", nativeName: "Português", flag: "🇵🇹" },
  { code: "nl", name: "Dutch", nativeName: "Nederlands", flag: "🇳🇱" },
  { code: "pl", name: "Polish", nativeName: "Polski", flag: "🇵🇱" },
  { code: "ru", name: "Russian", nativeName: "Русский", flag: "🇷🇺" },
  { code: "uk", name: "Ukrainian", nativeName: "Українська", flag: "🇺🇦" },
  { code: "cs", name: "Czech", nativeName: "Čeština", flag: "🇨🇿" },
  { code: "sk", name: "Slovak", nativeName: "Slovenčina", flag: "🇸🇰" },
  { code: "hu", name: "Hungarian", nativeName: "Magyar", flag: "🇭🇺" },
  { code: "ro", name: "Romanian", nativeName: "Română", flag: "🇷🇴" },
  { code: "bg", name: "Bulgarian", nativeName: "Български", flag: "🇧🇬" },
  { code: "hr", name: "Croatian", nativeName: "Hrvatski", flag: "🇭🇷" },
  { code: "sr", name: "Serbian", nativeName: "Српски", flag: "🇷🇸" },
  { code: "sl", name: "Slovenian", nativeName: "Slovenščina", flag: "🇸🇮" },
  { code: "el", name: "Greek", nativeName: "Ελληνικά", flag: "🇬🇷" },
  { code: "tr", name: "Turkish", nativeName: "Türkçe", flag: "🇹🇷" },
  { code: "da", name: "Danish", nativeName: "Dansk", flag: "🇩🇰" },
  { code: "sv", name: "Swedish", nativeName: "Svenska", flag: "🇸🇪" },
  { code: "no", name: "Norwegian", nativeName: "Norsk", flag: "🇳🇴" },
  { code: "fi", name: "Finnish", nativeName: "Suomi", flag: "🇫🇮" },
  { code: "et", name: "Estonian", nativeName: "Eesti", flag: "🇪🇪" },
  { code: "lv", name: "Latvian", nativeName: "Latviešu", flag: "🇱🇻" },
  { code: "lt", name: "Lithuanian", nativeName: "Lietuvių", flag: "🇱🇹" },
  { code: "ja", name: "Japanese", nativeName: "日本語", flag: "🇯🇵" },
  { code: "ko", name: "Korean", nativeName: "한국어", flag: "🇰🇷" },
  { code: "zh", name: "Chinese", nativeName: "中文", flag: "🇨🇳" },
  { code: "th", name: "Thai", nativeName: "ไทย", flag: "🇹🇭" },
  { code: "vi", name: "Vietnamese", nativeName: "Tiếng Việt", flag: "🇻🇳" },
  { code: "id", name: "Indonesian", nativeName: "Bahasa Indonesia", flag: "🇮🇩" },
  { code: "ar", name: "Arabic", nativeName: "العربية", flag: "🇸🇦" },
  { code: "he", name: "Hebrew", nativeName: "עברית", flag: "🇮🇱" },
  { code: "hi", name: "Hindi", nativeName: "हिन्दी", flag: "🇮🇳" },
  { code: "bn", name: "Bengali", nativeName: "বাংলা", flag: "🇧🇩" },
  { code: "sw", name: "Swahili", nativeName: "Kiswahili", flag: "🇰🇪" },
];

/**
 * Get translation for a key
 * @param {string} key - Translation key
 * @param {string} language - Language code (en, es, ja, ko)
 * @param {string} fallback - Fallback text if key not found
 * @returns {string} Translated text
 */
export const getTranslation = (key, language = "en", fallback = "") => {
  const langTranslations = TRANSLATIONS[language] || TRANSLATIONS.en;
  return langTranslations[key] || TRANSLATIONS.en[key] || fallback || key;
};

/**
 * Get all translations for a language
 * @param {string} language - Language code
 * @returns {Object} All translations for the language
 */
export const getLanguageTranslations = (language = "en") => {
  return TRANSLATIONS[language] || TRANSLATIONS.en;
};

/**
 * Check if a language is supported
 * @param {string} language - Language code
 * @returns {boolean} Whether the language is supported
 */
export const isLanguageSupported = (language) => {
  return SUPPORTED_LANGUAGES.some((lang) => lang.code === language);
};

/**
 * Get browser's preferred language that we support
 * @returns {string} Supported language code or 'en' as default
 */
export const getBrowserLanguage = () => {
  const browserLang = navigator.language?.split("-")[0] || "en";
  return isLanguageSupported(browserLang) ? browserLang : "en";
};

// For future integration with Google Cloud Translation API
// export const translateDynamic = async (text, targetLang, sourceLang = 'en') => {
//   // Would call GCP Translation API here
//   return text;
// };

export default {
  getTranslation,
  getLanguageTranslations,
  isLanguageSupported,
  getBrowserLanguage,
  SUPPORTED_LANGUAGES,
};
